import type { WorkspaceRole } from '../types/workspace'
import type { CustomRole } from '../types/customRole'

export type WorkspaceAction =
  | 'workspace.view_settings' | 'workspace.edit_settings' | 'workspace.manage_members' | 'workspace.manage_invites'
  | 'workspace.manage_field_passes' | 'workspace.manage_roles' | 'workspace.manage_permissions' | 'workspace.manage_credits' | 'workspace.view_reports'
  | 'workspace.view_analysis' | 'workspace.delete_data' | 'workspace.manage_owners' | 'workspace.delete' | 'billing.manage' | 'workspace.edit_team_settings'
  | 'timeline.view' | 'timeline.create' | 'timeline.edit' | 'timeline.delete' | 'timeline.move_restore'
  | 'board.edit' | 'board.move_items' | 'board.delete_forever' | 'checklist.create' | 'checklist.edit_structure'
  | 'checklist.delete' | 'checklist.edit_canvas' | 'checklist.assign' | 'checklist.change_status'
  | 'checklist.manage_proof' | 'checklist.manage_memo' | 'checklist.comment' | 'checklist.location_checkin'
  | 'chat.view' | 'chat.send' | 'chat.huddle' | 'chat.create_channel' | 'chat.create_private_channel'
  | 'chat.view_archive' | 'chat.manage_archive'
  | 'ai.use' | 'raid.participate' | 'raid.manage'
  | 'public.publish' | 'public.manage_join_requests'
  | 'forms.view' | 'forms.design' | 'forms.submit' | 'forms.review_responses' | 'forms.manage'
  | 'meetings.view' | 'meetings.schedule' | 'meetings.join' | 'meetings.manage'
  | 'integrations.view' | 'integrations.manage' | 'integrations.sync' | 'integrations.disconnect' | 'integrations.hard_reset'
  | 'automation.view' | 'automation.manage'
  | 'overview.view' | 'overview.edit'
  | 'chart.manage'
  | 'readonly.view_usage' | 'readonly.view_billing' | 'readonly.view_member_contact_info' | 'readonly.view_uploads'
  | 'readonly.view_ai_history' | 'readonly.view_private_boards' | 'readonly.view_activity_logs'
  | 'readonly.view_reports' | 'readonly.view_analysis' | 'readonly.view_audit_logs' | 'readonly.view_invite_links' | 'readonly.view_settings'

export type WorkspaceActionPermissions = Record<WorkspaceAction, WorkspaceRole>
export type BoardWorkspaceRole = Exclude<WorkspaceRole, 'HELPER' | 'LEARNER'>

export type WorkspacePermissionGroup = {
  key: string
  label: string
  description: string
  actions: Array<{ action: WorkspaceAction; label: string; detail: string }>
}

export const WORKSPACE_PERMISSION_GROUPS: WorkspacePermissionGroup[] = [
  { key: 'workspace', label: 'Workspace administration', description: 'Settings, people, finance, reports, and destructive operations.', actions: [
    { action: 'workspace.view_settings', label: 'View workspace settings', detail: 'Open workspace configuration and audit summaries.' },
    { action: 'workspace.edit_settings', label: 'Edit workspace settings', detail: 'Change views, limits, audit retention, and workspace configuration.' },
    { action: 'workspace.manage_members', label: 'Manage members', detail: 'View and administer the active member roster.' },
    { action: 'workspace.manage_invites', label: 'Manage invites', detail: 'Create, revoke, revive, and respond to invitations.' },
    { action: 'workspace.manage_field_passes', label: 'Manage Field Passes', detail: 'Create and revoke temporary burner access for assigned work.' },
    { action: 'workspace.manage_roles', label: 'Change member roles', detail: 'Promote or demote workspace members.' },
    { action: 'workspace.manage_permissions', label: 'Configure permissions', detail: 'Change this workspace action policy.' },
    { action: 'workspace.manage_credits', label: 'Manage credits', detail: 'Transfer and allocate workspace credits.' },
    { action: 'workspace.view_reports', label: 'Generate and view reports', detail: 'Use summary, audit, and report generation tools.' },
    { action: 'workspace.view_analysis', label: 'View workspace analysis', detail: 'Access workspace intelligence: health, blockers, drift, readiness, ops, governance, and AI.' },
    { action: 'chart.manage', label: 'Manage custom charts', detail: 'Create, edit, copy, and delete custom charts.' },
    { action: 'workspace.delete_data', label: 'Delete workspace data', detail: 'Run destructive workspace lifecycle operations.' },
    { action: 'workspace.manage_owners', label: 'Manage owners', detail: 'Manage workspace owners and transfers.' },
    { action: 'workspace.delete', label: 'Delete or hand over the workspace', detail: 'Delete or hand over the workspace.' },
    { action: 'billing.manage', label: 'Billing, purchases and seats', detail: 'Billing, purchases, and workspace seats.' },
    { action: 'workspace.edit_team_settings', label: 'Edit team settings', detail: 'Notifications, helper visibility, raid blueprints, checklist policy, games, timesheets.' },
  ]},
  { key: 'readonly-sensitive', label: 'Read-only sensitive visibility', description: 'Optional visibility grants for Read-only members. These never grant edit, add, or delete access.', actions: [
    { action: 'readonly.view_usage', label: 'View usage', detail: 'See workspace and member usage counters.' },
    { action: 'readonly.view_billing', label: 'View billing and credits', detail: 'See credit, wallet, subscription, and billing details.' },
    { action: 'readonly.view_member_contact_info', label: 'View member contact info', detail: 'See member emails, phone numbers, and extended profile/contact details.' },
    { action: 'readonly.view_uploads', label: 'View uploads', detail: 'Open uploaded files and Upload Hub artifacts.' },
    { action: 'readonly.view_ai_history', label: 'View AI history', detail: 'See AI chats, prompts, generated outputs, and AI-derived history.' },
    { action: 'readonly.view_private_boards', label: 'View private boards', detail: 'See boards or items marked private/internal.' },
    { action: 'readonly.view_activity_logs', label: 'View activity logs', detail: 'See activity feeds and operational logs.' },
    { action: 'readonly.view_reports', label: 'View reports', detail: 'See generated reports and report previews.' },
    { action: 'readonly.view_analysis', label: 'View analysis', detail: 'See reduced analysis dashboards without sensitive governance, finance, or AI signals.' },
    { action: 'readonly.view_audit_logs', label: 'View audit logs', detail: 'See raw audit events and audit-ledger details.' },
    { action: 'readonly.view_invite_links', label: 'View invite links', detail: 'See reusable invite links and invite tokens.' },
    { action: 'readonly.view_settings', label: 'View sensitive settings', detail: 'See settings that expose configuration, security, or integration details.' },
  ]},
  { key: 'timeline', label: 'Timeline', description: 'Goal, task, and subtask lifecycle.', actions: [
    { action: 'timeline.view', label: 'View timeline items', detail: 'Open and drill into goals, tasks, and subtasks.' },
    { action: 'timeline.create', label: 'Create timeline items', detail: 'Create goals, tasks, and subtasks.' },
    { action: 'timeline.edit', label: 'Edit timeline items', detail: 'Change timeline content and dates.' },
    { action: 'timeline.delete', label: 'Delete timeline items', detail: 'Move timeline records to Bin.' },
    { action: 'timeline.move_restore', label: 'Move and restore items', detail: 'Move records between boards and restore from Bin.' },
  ]},
  { key: 'boards', label: 'Boards and Explorer', description: 'Board content and permanent lifecycle operations.', actions: [
    { action: 'board.edit', label: 'Edit board content', detail: 'Create and edit Explorer folders and files.' },
    { action: 'board.move_items', label: 'Move board items', detail: 'Copy, cut, paste, drag, archive, and restore.' },
    { action: 'board.delete_forever', label: 'Delete forever', detail: 'Permanently remove Bin content.' },
  ]},
  { key: 'checklists', label: 'Checklists', description: 'Structure, canvas, and operational work controls.', actions: [
    { action: 'checklist.create', label: 'Create checklist items', detail: 'Add checklist nodes and gates.' },
    { action: 'checklist.edit_structure', label: 'Edit checklist structure', detail: 'Edit title, details, schedule, ratings, weight, and requirements.' },
    { action: 'checklist.delete', label: 'Delete checklist items', detail: 'Remove checklist nodes.' },
    { action: 'checklist.edit_canvas', label: 'Edit checklist canvas', detail: 'Move nodes, connect wires, and save canvas layout.' },
    { action: 'checklist.assign', label: 'Change assignee', detail: 'Assign or unassign a checklist item.' },
    { action: 'checklist.change_status', label: 'Change workflow status', detail: 'Move an item through its workflow.' },
    { action: 'checklist.manage_proof', label: 'Upload or remove proof', detail: 'Manage the current proof file.' },
    { action: 'checklist.manage_memo', label: 'Record or replace memo', detail: 'Manage one audio or video memo.' },
    { action: 'checklist.comment', label: 'Use discussions', detail: 'Post, mention, reply, react, attach, and manage owned comments.' },
    { action: 'checklist.location_checkin', label: 'Complete location check-in', detail: 'Submit location verification during completion.' },
  ]},
  { key: 'communication', label: 'Communication', description: 'Workspace chat and live collaboration.', actions: [
    { action: 'chat.view', label: 'View chat', detail: 'Open workspace chat history.' },
    { action: 'chat.send', label: 'Send chat messages', detail: 'Post text and attachments.' },
    { action: 'chat.huddle', label: 'Join huddles', detail: 'Use voice-presence controls.' },
    { action: 'chat.create_channel', label: 'Create public channels', detail: 'Spin up a new public chat channel anyone in the workspace can join.' },
    { action: 'chat.create_private_channel', label: 'Create private channels', detail: 'Spin up an invite-only channel with a curated member list.' },
    { action: 'chat.view_archive', label: 'View chat archive', detail: 'See channels removed from active chat.' },
    { action: 'chat.manage_archive', label: 'Manage chat archive', detail: 'Restore archived channels or permanently remove archived chat records.' },
  ]},
  { key: 'ai-raids', label: 'AI and raids', description: 'Generation tools and cooperative raids.', actions: [
    { action: 'ai.use', label: 'Use AI Magic', detail: 'Generate and edit timelines with AI credits.' },
    { action: 'raid.participate', label: 'Participate in raids', detail: 'View raids and apply work damage.' },
    { action: 'raid.manage', label: 'Manage raids', detail: 'Start and resolve raids.' },
  ]},
  { key: 'integrations', label: 'Link Hub', description: 'External connections, deterministic exports, and destructive connection lifecycle.', actions: [
    { action: 'integrations.view', label: 'View Link Hub', detail: 'View connections, scoped targets, conflicts, and sync activity.' },
    { action: 'integrations.manage', label: 'Manage integrations', detail: 'Connect providers and configure target scopes and mappings.' },
    { action: 'integrations.sync', label: 'Run synchronization', detail: 'Queue manual provider exports.' },
    { action: 'integrations.disconnect', label: 'Disconnect integrations', detail: 'Revoke provider authorization for a shared connection.' },
    { action: 'integrations.hard_reset', label: 'Hard reset integrations', detail: 'Revoke and purge every integration record for this workspace.' },
  ]},
  { key: 'forms', label: 'Forms', description: 'Workspace intake design, submissions, and response governance.', actions: [
    { action: 'forms.view', label: 'View Forms', detail: 'Open form definitions and submission tracking.' },
    { action: 'forms.design', label: 'Design forms', detail: 'Create and update form structures, logic, and themes.' },
    { action: 'forms.submit', label: 'Submit forms', detail: 'Send responses to active workspace forms.' },
    { action: 'forms.review_responses', label: 'Review form responses', detail: 'Read submitted responses from other workspace members.' },
    { action: 'forms.manage', label: 'Manage forms', detail: 'Archive definitions and review or export responses.' },
  ]},
  { key: 'meetings', label: 'Meetings', description: 'Scheduling, joining, agenda context, and provider lifecycle.', actions: [
    { action: 'meetings.view', label: 'View meetings', detail: 'Open the meeting stream and contextual sidecar.' },
    { action: 'meetings.schedule', label: 'Schedule meetings', detail: 'Create meetings and use the scheduling concierge.' },
    { action: 'meetings.join', label: 'Join meetings', detail: 'Use provider join gateways and RSVP controls.' },
    { action: 'meetings.manage', label: 'Manage meetings', detail: 'Edit agendas, synchronize status, and cancel meetings.' },
  ]},
  { key: 'automation', label: 'Automate Hub', description: 'Workflow definitions and the workspace automation engine.', actions: [
    { action: 'automation.view', label: 'View Automate Hub', detail: 'See automation workflows and their run history.' },
    { action: 'automation.manage', label: 'Manage automations', detail: 'Create, edit, pause, and delete workflow automations.' },
  ]},
  { key: 'overview', label: 'Overview', description: 'The workspace Overview: people, progress, and custom sections.', actions: [
    { action: 'overview.view', label: 'View Overview', detail: 'Open the workspace Overview page.' },
    { action: 'overview.edit', label: 'Customize Overview', detail: 'Customize: reorder and hide sections, edit the brief, add custom sections.' },
  ]},
  { key: 'public', label: 'Public workspace', description: 'Publishing and access-request administration.', actions: [
    { action: 'public.publish', label: 'Publish workspace', detail: 'Change public workspace availability.' },
    { action: 'public.manage_join_requests', label: 'Manage join requests', detail: 'Approve or reject access requests.' },
  ]},
]

const editorActions = new Set<WorkspaceAction>([
  // NOTE: `workspace.manage_invites` was previously defaulted to EDITOR here,
  // but firestore.rules gates the membership write at ADMIN, so the UI was
  // showing the "+ Invite member" button to EDITORs whose writes would then
  // fail at the database. Aligned to ADMIN (the rules fallback). Existing
  // workspaces with the old EDITOR override on `actionPermissions` retain
  // their stored value; admins can tighten via the Permissions panel.
  'timeline.create', 'timeline.edit', 'timeline.delete', 'timeline.move_restore',
  'board.edit', 'board.move_items', 'board.delete_forever', 'checklist.create', 'checklist.edit_structure',
  'checklist.delete', 'checklist.edit_canvas', 'ai.use', 'raid.manage', 'public.manage_join_requests',
  'integrations.view', 'integrations.sync', 'forms.design', 'forms.manage',
  'meetings.schedule', 'meetings.manage',
  'chat.create_private_channel',
  'chat.view_archive',
  'workspace.manage_field_passes',
  'workspace.view_analysis',
  'chart.manage',
  'overview.edit',
])
const viewerActions = new Set<WorkspaceAction>([
  'workspace.view_settings', 'timeline.view', 'checklist.assign', 'checklist.change_status', 'checklist.manage_proof',
  'checklist.manage_memo', 'checklist.comment', 'checklist.location_checkin', 'chat.view', 'chat.send',
  'chat.huddle', 'chat.create_channel', 'raid.participate',
  'forms.view', 'forms.submit',
  'meetings.view', 'meetings.join',
  'automation.view',
  'readonly.view_analysis',
])
export const getViewerActions = (): ReadonlySet<WorkspaceAction> => viewerActions
export const isViewerAction = (action: WorkspaceAction | string): boolean => viewerActions.has(action as WorkspaceAction)
const helperActions = new Set<WorkspaceAction>([
  'overview.view', 'chat.view', 'chat.send', 'chat.huddle',
  'meetings.view', 'meetings.join',
])
const readonlyActions = new Set<WorkspaceAction>([
  'timeline.view', 'overview.view',
  'forms.view',
  'meetings.view', 'automation.view',
])
const readonlySensitiveActions = new Set<WorkspaceAction>([
  'readonly.view_usage', 'readonly.view_billing', 'readonly.view_member_contact_info', 'readonly.view_uploads',
  'readonly.view_ai_history', 'readonly.view_private_boards', 'readonly.view_activity_logs',
  'readonly.view_reports', 'readonly.view_analysis', 'readonly.view_audit_logs', 'readonly.view_invite_links', 'readonly.view_settings',
])

export const OWNER_ONLY_ACTIONS = new Set<WorkspaceAction>([
  'workspace.edit_settings',
  'public.publish',
  'workspace.manage_owners',
  'workspace.delete',
  'billing.manage',
])

export const DEFAULT_WORKSPACE_ACTION_PERMISSIONS = Object.fromEntries(
  WORKSPACE_PERMISSION_GROUPS.flatMap((group) => group.actions).map(({ action }) => [
    action,
    OWNER_ONLY_ACTIONS.has(action) ? 'OWNER'
      : readonlyActions.has(action) ? 'READONLY'
        : helperActions.has(action) ? 'HELPER'
          : viewerActions.has(action) ? 'VIEWER'
            : editorActions.has(action) ? 'EDITOR'
              : 'ADMIN',
  ]),
) as WorkspaceActionPermissions

const ADMIN_ONLY_ACTIONS = new Set<WorkspaceAction>([
  'integrations.manage', 'integrations.disconnect', 'integrations.hard_reset',
])

export const normalizeWorkspaceActionPermissions = (value: unknown): WorkspaceActionPermissions => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  return Object.fromEntries(Object.entries(DEFAULT_WORKSPACE_ACTION_PERMISSIONS).map(([action, fallback]) => {
    const workspaceAction = action as WorkspaceAction
    if (OWNER_ONLY_ACTIONS.has(workspaceAction)) return [action, 'OWNER']
    if (ADMIN_ONLY_ACTIONS.has(workspaceAction)) return [action, 'ADMIN']
    const candidate = source[action]
    if (candidate === 'VIEWER' && readonlyActions.has(workspaceAction)) return [action, 'READONLY']
    if (candidate === 'READONLY' && canSetWorkspaceActionToReadonly(workspaceAction)) {
      return [action, candidate]
    }
    if (candidate === 'HELPER' && canSetWorkspaceActionToHelper(workspaceAction)) {
      return [action, candidate]
    }
    return [action, candidate === 'OWNER' || candidate === 'ADMIN' || candidate === 'EDITOR' || candidate === 'VIEWER' ? candidate : fallback]
  })) as WorkspaceActionPermissions
}

export const ROLE_RANK: Record<WorkspaceRole, number> = { LEARNER: -1, HELPER: 0, READONLY: 1, VIEWER: 2, EDITOR: 3, ADMIN: 4, OWNER: 5 }

export const roleCanPerform = (role: WorkspaceRole, requiredRole: WorkspaceRole) =>
  role !== 'LEARNER' && !(role === 'READONLY' && requiredRole === 'HELPER') && ROLE_RANK[role] >= ROLE_RANK[requiredRole]

export const isWorkspaceAction = (action: unknown): action is WorkspaceAction =>
  typeof action === 'string' && action in DEFAULT_WORKSPACE_ACTION_PERMISSIONS

export const isWorkspaceRole = (role: unknown): role is WorkspaceRole =>
  role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR' || role === 'VIEWER' || role === 'READONLY' || role === 'HELPER' || role === 'LEARNER'

export const isBoardWorkspaceRole = (role: unknown): role is BoardWorkspaceRole =>
  role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR' || role === 'VIEWER' || role === 'READONLY'

export const parseWorkspaceRole = (value: unknown): WorkspaceRole | null =>
  isWorkspaceRole(value) ? value : null

export const parseNonHelperRole = (value: unknown): BoardWorkspaceRole | null =>
  isBoardWorkspaceRole(value) ? value : null

export const normalizeBoardWorkspaceRole = (role: unknown): BoardWorkspaceRole =>
  isBoardWorkspaceRole(role) ? role : 'VIEWER'

export const canSetWorkspaceActionToReadonly = (action: WorkspaceAction): boolean =>
  readonlyActions.has(action) || readonlySensitiveActions.has(action)

export const canSetWorkspaceActionToHelper = (action: WorkspaceAction): boolean =>
  helperActions.has(action)

/**
 * Given the workspace's default minimum role for an action, plus a
 * member's optional custom role, resolve the *effective minimum role*
 * required for that action for members carrying this custom role.
 *
 * Overrides in a custom role act like the Permission Manager rules —
 * per-action minimums. They can only *tighten* (raise the required
 * rank), never loosen: firestore.rules only sees `membership.role`
 * (the base) so a loosened UI check would let the client attempt a
 * write the server would then reject. Reject any loosening override
 * up-front instead — never grant more power than the workspace default.
 */
export const resolveActionRequiredRole = (
  workspaceRequired: WorkspaceRole,
  action: WorkspaceAction,
  customRole: CustomRole | undefined | null,
): WorkspaceRole => {
  if (!customRole) return workspaceRequired
  const override = customRole.overrides?.[action]
  if (!override) return workspaceRequired
  const overrideRank = ROLE_RANK[override] ?? 0
  const defaultRank = ROLE_RANK[workspaceRequired] ?? 0
  // Only accept overrides that raise the required rank (tighten access).
  return overrideRank >= defaultRank ? override : workspaceRequired
}

/**
 * Convenience: given a member's base role + custom role, does the
 * member clear the requirement for `action`? Uses
 * `resolveActionRequiredRole` and then `roleCanPerform`.
 */
export const memberCanPerform = (
  baseRole: WorkspaceRole,
  action: WorkspaceAction,
  workspaceRequired: WorkspaceRole,
  customRole: CustomRole | undefined | null,
): boolean => {
  const required = resolveActionRequiredRole(workspaceRequired, action, customRole)
  return roleCanPerform(baseRole, required)
}

/**
 * Lookup helper: find a custom role by id in a workspace's custom
 * role list. Returns null when not found or when list is undefined.
 */
export const findCustomRole = (
  customRoles: CustomRole[] | undefined,
  id: string | undefined | null,
): CustomRole | null => {
  if (!id || !customRoles) return null
  return customRoles.find((role) => role.id === id) ?? null
}
